import apiClient from './apiClient';

export type QcRunStatus = 'in_progress' | 'completed' | 'signed_off' | 'voided';
export type QcOverallResult = 'pass' | 'pass_with_observations' | 'fail';
export type QcItemResultValue = 'pass' | 'fail' | 'na';

export interface QcChecklistItem {
  text: string;
  critical: boolean;
  requiresEvidence: boolean;
}

export interface QcTemplate {
  id: string;
  projectId: string | null;
  name: string;
  description: string | null;
  appliesToTaskType: string | null;
  items: QcChecklistItem[];
  version: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface QcItemResult {
  id: string;
  runId: string;
  itemIndex: number;
  result: QcItemResultValue | null;
  notes: string | null;
  evidenceDocumentId: string | null;
  evidenceFieldId: string | null;
  recordedAt: string | null;
}

export interface QcSignature {
  id: string;
  runId: string;
  signedByUserId: string;
  roleAtSigning: string;
  attestation: string;
  signatureDataUrl: string | null;
  signedAt: string;
}

export interface QcRun {
  id: string;
  templateId: string;
  taskId: string;
  projectId: string;
  runCode: string;
  templateVersion: number;
  itemsSnapshot: QcChecklistItem[];
  status: QcRunStatus;
  overallResult: QcOverallResult | null;
  startedAt: string;
  completedAt: string | null;
  notes: string | null;
  template?: Pick<QcTemplate, 'id' | 'name' | 'version' | 'appliesToTaskType'>;
  task?: { id: string; taskCode?: string; name?: string; taskType?: string };
  itemResults?: QcItemResult[];
  signatures?: QcSignature[];
}

export interface QcTemplatePayload {
  projectId?: string | null;
  name: string;
  description?: string | null;
  appliesToTaskType?: string | null;
  items: QcChecklistItem[];
}

type ApiEnvelope<T> = { data?: T };

const unwrap = <T>(response: any): T => {
  const body = response?.data ?? response;
  return Object.prototype.hasOwnProperty.call(body, 'data') ? (body as ApiEnvelope<T>).data as T : body as T;
};

class QcChecklistService {
  async listTemplates(params: { projectId?: string; taskType?: string; active?: boolean } = {}): Promise<QcTemplate[]> {
    return unwrap<QcTemplate[]>(await apiClient.get('/qc-templates', { params }));
  }

  async createTemplate(payload: QcTemplatePayload): Promise<QcTemplate> {
    return unwrap<QcTemplate>(await apiClient.post('/qc-templates', payload));
  }

  async updateTemplate(id: string, payload: Partial<QcTemplatePayload>): Promise<QcTemplate> {
    return unwrap<QcTemplate>(await apiClient.patch(`/qc-templates/${id}`, payload));
  }

  async setTemplateActive(id: string, active: boolean): Promise<QcTemplate> {
    return unwrap<QcTemplate>(await apiClient.patch(`/qc-templates/${id}/active`, { active }));
  }

  async listRuns(params: { projectId?: string; status?: QcRunStatus; result?: QcOverallResult; templateId?: string } = {}): Promise<QcRun[]> {
    return unwrap<QcRun[]>(await apiClient.get('/qc-runs', { params }));
  }

  async getRun(id: string): Promise<QcRun> {
    return unwrap<QcRun>(await apiClient.get(`/qc-runs/${id}`));
  }

  async listRunsForTask(taskId: string): Promise<QcRun[]> {
    return unwrap<QcRun[]>(await apiClient.get(`/tasks/${taskId}/qc-runs`));
  }

  async startRun(taskId: string, templateId: string): Promise<QcRun> {
    return unwrap<QcRun>(await apiClient.post(`/tasks/${taskId}/qc-runs`, { templateId }));
  }

  async recordItemResult(runId: string, itemIndex: number, payload: {
    result: QcItemResultValue;
    notes?: string | null;
    evidenceDocumentId?: string | null;
    evidenceFieldId?: string | null;
  }): Promise<QcItemResult> {
    return unwrap<QcItemResult>(await apiClient.patch(`/qc-runs/${runId}/items/${itemIndex}`, payload));
  }

  async completeRun(id: string): Promise<QcRun> {
    return unwrap<QcRun>(await apiClient.post(`/qc-runs/${id}/complete`));
  }

  async signRun(id: string, payload: { attestation: string; signatureDataUrl?: string | null }): Promise<QcRun> {
    return unwrap<QcRun>(await apiClient.post(`/qc-runs/${id}/sign`, payload));
  }

  async voidRun(id: string, reason: string): Promise<QcRun> {
    return unwrap<QcRun>(await apiClient.post(`/qc-runs/${id}/void`, { reason }));
  }
}

export default new QcChecklistService();
